import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ASSETS, SERVICES, COMPANY_INFO } from '../config/assets';
import { CheckCircle, Hammer, Settings, Zap, AlertTriangle, ArrowRight, Wrench, Mail, Phone, MapPin, Send, HardHat, Target, ChevronLeft, ChevronRight, Share } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const ConcreteGrindingPage = () => {
  const service = SERVICES.find(s => s.slug === 'concrete-grinding-floor-prep');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    serviceType: 'Concrete Grinding Floor Prep'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // All project images from the previous website
  const allProjectImages = [
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250216-WA0007-scaled.jpg', title: 'Commercial Grinding Project', description: 'Large scale concrete surface preparation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0001-scaled.jpg', title: 'Adhesive Removal Process', description: 'Professional tile glue grinding and removal' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0003-scaled.jpg', title: 'Floor Leveling Work', description: 'High point removal and surface flattening' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0005-scaled.jpg', title: 'Industrial Grinding Equipment', description: 'Professional diamond grinding machinery in action' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0007-scaled.jpg', title: 'Surface Preparation Detail', description: 'Close-up view of professional grinding results' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0009-scaled.jpg', title: 'Before & After Comparison', description: 'Dramatic transformation through proper preparation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0011-scaled.jpg', title: 'Coating Removal Project', description: 'Failed waterproofing membrane removal' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0014-scaled.jpg', title: 'Carpet Glue Removal', description: 'Professional adhesive grinding service' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0015-scaled.jpg', title: 'Equipment in Action', description: 'Heavy-duty grinding machinery at work' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0018-scaled.jpg', title: 'Surface Profile Creation', description: 'Achieving optimal surface texture for new finishes' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0019-scaled.jpg', title: 'Industrial Floor Prep', description: 'Large warehouse surface preparation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0021-scaled.jpg', title: 'Precision Grinding Work', description: 'Detailed surface preparation techniques' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0023-scaled.jpg', title: 'Slab Flattening Process', description: 'Professional slab leveling and preparation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0025-scaled.jpg', title: 'Contamination Removal', description: 'Clean substrate preparation for optimal results' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0027-scaled.jpg', title: 'Quality Control Check', description: 'Surface inspection and profile verification' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0029-scaled.jpg', title: 'Tile Adhesive Grinding', description: 'Complete adhesive removal process' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0031-scaled.jpg', title: 'Commercial Space Prep', description: 'Retail space floor preparation project' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0034-scaled.jpg', title: 'High Point Removal', description: 'Precision grinding for level surfaces' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0035-scaled.jpg', title: 'Equipment Detail View', description: 'Professional grinding equipment close-up' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0038-scaled.jpg', title: 'Surface Texture Work', description: 'Creating optimal surface profile for finishes' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0039-scaled.jpg', title: 'Project Progress View', description: 'Mid-project grinding progress documentation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0041-scaled.jpg', title: 'Failed Coating Removal', description: 'Professional stripping of failed systems' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0044-scaled.jpg', title: 'Dust Control System', description: 'HEPA filtration during grinding operations' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/IMG-20250312-WA0045-scaled.jpg', title: 'Edge Work Detail', description: 'Precision edge grinding and preparation' }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allProjectImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allProjectImages.length) % allProjectImages.length);
  };

  const goToSlide = (index) => {
    setCurrentImageIndex(index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Thank you! Your message has been sent successfully.');
        setFormData({ name: '', email: '', phone: '', message: '', serviceType: 'Concrete Grinding Floor Prep' });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const keyServices = [
    { icon: Hammer, title: 'Adhesive Removal', description: 'Complete removal of carpet glue, tile adhesive, and stubborn bonding agents' },
    { icon: Settings, title: 'Surface Leveling', description: 'Grinding high points and flattening uneven slabs for optimal finish application' },
    { icon: AlertTriangle, title: 'Coating Stripping', description: 'Professional removal of failed coatings, sealers, and waterproofing membranes' },
    { icon: Target, title: 'Profile Creation', description: 'Achieving the correct surface profile for maximum adhesion of new finishes' }
  ];

  const removalServices = [
    'Carpet glue and adhesive residue',
    'Tile adhesive and mortar',
    'Failed waterproofing membranes',
    'Old epoxy and coating systems',
    'Paint and surface contaminants',
    'Rubber flooring adhesives',
    'VCT tile and adhesive',
    'Construction debris and spills'
  ];

  const surfacePreparation = [
    { issue: 'High Points & Ridges', solution: 'Precision grinding to create flat, even surfaces' },
    { issue: 'Adhesive Contamination', solution: 'Complete removal ensuring clean substrate for new finishes' },
    { issue: 'Failed Coatings', solution: 'Strip back to sound concrete for reliable adhesion' },
    { issue: 'Surface Laitance', solution: 'Remove weak surface layer to expose strong concrete' },
    { issue: 'Uneven Slab Levels', solution: 'Flatten within achievable tolerances for your application' },
    { issue: 'Joint Sealant Removal', solution: 'Clean preparation for new joint treatments' }
  ];

  const whyEssential = [
    { title: 'Adhesion Critical', description: 'New finishes require clean, profiled surfaces to bond properly and last' },
    { title: 'Failure Prevention', description: 'Poor preparation is the #1 cause of coating and overlay failures' },
    { title: 'Cost Effective', description: 'Proper prep prevents costly re-work and premature finish replacement' },
    { title: 'Professional Results', description: 'Only industrial diamond grinding achieves the surface quality needed' },
    { title: 'Time Saving', description: 'Efficient removal of multiple layers and contaminants in one process' },
    { title: 'Warranty Protection', description: 'Most finish warranties require professional surface preparation' }
  ];

  const equipmentSpecs = [
    { spec: 'Diamond Tooling', detail: 'Industrial-grade diamond grinding wheels for all concrete types' },
    { spec: 'Dust Collection', detail: 'HEPA filtration systems for clean, safe working environment' },
    { spec: 'Variable Speed', detail: 'Adjustable grinding speeds for different materials and applications' },
    { spec: 'Edge Access', detail: 'Specialized equipment for corners, edges, and tight spaces' },
    { spec: 'Profile Control', detail: 'Precise control over surface texture and aggregate exposure' },
    { spec: 'Debris Management', detail: 'Integrated vacuum systems for continuous material removal' }
  ];

  const processSteps = [
    { step: '01', title: 'Site Assessment', description: 'Evaluate existing conditions, contamination levels, and required preparation scope' },
    { step: '02', title: 'Equipment Setup', description: 'Deploy appropriate grinding equipment and dust collection systems' },
    { step: '03', title: 'Systematic Grinding', description: 'Progressive material removal using diamond tooling matched to substrate' },
    { step: '04', title: 'Profile Verification', description: 'Check surface profile and cleanliness against specification requirements' },
    { step: '05', title: 'Final Inspection', description: 'Quality control ensuring surface is ready for next phase application' }
  ];

  // Sample YouTube video data - you can update these with actual video IDs
  const youtubeVideos = [
    {
      id: 'tUu5zh9mKdg', // Actual video from your channel
      title: 'Carpet Glue Removal: Large Commercial Space',
      description: 'Watch our team tackle extensive carpet adhesive removal'
    },
    {
      id: 'ycqiyHTLI60', // Actual video from your channel
      title: 'Tile Adhesive Grinding Process',
      description: 'Professional tile glue removal techniques and equipment'
    },
    {
      id: 'SDKO97wf-8U', // Actual video from your channel
      title: 'Failed Coating Removal Case Study',
      description: 'How we strip failed waterproofing for successful reapplication'
    }
  ];

  // Sample YouTube Shorts data - you can update these with actual video IDs
  const youtubeShorts = [
    {
      id: '0H9nhGOU4SI', // Actual short from your channel
      title: 'Floor Prep Basics',
      description: 'Essential surface preparation tips'
    },
    {
      id: 'qS1zDF5SlYA', // Actual short from your channel
      title: 'Equipment Overview',
      description: 'Professional grinding equipment'
    },
    {
      id: 'vEOVe98Qhlo', // Actual short from your channel
      title: 'Before & After',
      description: 'Amazing prep transformations'
    },
    {
      id: 'CDs_Ki5hYUM', // Actual short from your channel
      title: 'Adhesive Removal',
      description: 'Carpet & tile glue grinding'
    },
    {
      id: 'By4PznY49Ic', // Actual short from your channel
      title: 'Surface Leveling',
      description: 'Flattening concrete slabs'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700">
      {/* Hero Video Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="concrete-grinding-hero-video"
        >
          <source src={service.video} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Video Overlay */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />
        
        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            className="max-w-4xl"
          >
            <Badge variant="secondary" className="mb-4 bg-white/20 text-white border-white/30">
              Foundation Service
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              Concrete Grinding
              <span className="bg-gradient-to-r from-orange-300 to-red-400 bg-clip-text text-transparent block">
                Floor Prep
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-100 mb-8 max-w-2xl mx-auto leading-relaxed">
              The essential foundation for flawless finishes. Professional surface preparation that ensures your investment lasts.
            </p>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white border-0 shadow-2xl"
              asChild
            >
              <Link to="/contact" data-testid="hero-cta-button">
                Get Free Assessment <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        {/* Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
              The Foundation of Every Great Finish
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
              Concrete grinding and floor preparation is the critical first step that determines the success of any flooring project. 
              We specialize in removing carpet glue, tile adhesive, failed waterproofing membranes, and old coatings that prevent 
              new finishes from adhering properly.
            </p>
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Our precision grinding services flatten uneven slabs, remove high points, and create the perfect surface profile for 
              your chosen finish. While we can't achieve perfectly level surfaces, we can flatten them within industry tolerances 
              to ensure optimal results for polished concrete, overlays, and protective coatings.
            </p>
          </div>
        </motion.section>

        {/* Key Services */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Essential Preparation Services
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-orange-200 dark:border-orange-800 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950 dark:to-red-950">
                  <CardContent className="p-6 text-center">
                    <service.icon className="w-12 h-12 text-orange-600 mx-auto mb-4" />
                    <h4 className="font-semibold mb-2 text-slate-800 dark:text-slate-100">
                      {service.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* What We Remove */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6 text-slate-800 dark:text-slate-100">
                Complete Contamination Removal
              </h3>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
                Our industrial diamond grinding equipment removes stubborn adhesives, failed coatings, and surface 
                contaminants that prevent proper adhesion. No matter what's on your concrete, we have the tools 
                and expertise to restore it to a clean, bondable substrate.
              </p>
              <div className="grid gap-3">
                {removalServices.map((service, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-200">{service}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <Card className="p-6 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 border-slate-300 dark:border-slate-600">
                <h4 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-100">
                  Common Surface Problems We Solve
                </h4>
                <div className="space-y-4">
                  {surfacePreparation.map((item, index) => (
                    <div key={index} className="p-3 bg-orange-50 dark:bg-orange-950 rounded-lg border border-orange-200 dark:border-orange-800">
                      <h5 className="font-medium text-orange-800 dark:text-orange-200 mb-1">{item.issue}</h5>
                      <p className="text-sm text-orange-700 dark:text-orange-300">{item.solution}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </motion.section>

        {/* Why Essential */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Why Professional Preparation is Essential
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyEssential.map((reason, index) => (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <CheckCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100">
                        {reason.title}
                      </h4>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {reason.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Equipment & Technology */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Professional Grade Equipment
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {equipmentSpecs.map((equipment, index) => (
              <motion.div
                key={equipment.spec}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <HardHat className="w-6 h-6 text-orange-600 flex-shrink-0" />
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100">
                        {equipment.spec}
                      </h4>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {equipment.detail}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 max-w-4xl mx-auto">
            <Card className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950 dark:to-orange-950 border-amber-200 dark:border-amber-800">
              <div className="flex items-start space-x-3">
                <Zap className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Industrial Capability</h4>
                  <p className="text-amber-700 dark:text-amber-300">
                    Our fleet includes walk-behind grinders, ride-on planetary systems, and specialized edge equipment. 
                    This allows us to handle everything from residential rooms to large commercial spaces efficiently 
                    and with consistent results.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </motion.section>

        {/* Process */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Our Systematic Approach
          </h3>
          <div className="max-w-4xl mx-auto">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start mb-8 last:mb-0"
              >
                <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg mr-6 flex-shrink-0">
                  {step.step}
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2 text-slate-800 dark:text-slate-100">
                    {step.title}
                  </h4>
                  <p className="text-slate-600 dark:text-slate-300">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Project Gallery Carousel */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Floor Prep Project Gallery
          </h3>
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden">
              <div className="relative">
                {/* Main Image Display */}
                <div className="aspect-video relative overflow-hidden bg-slate-100 dark:bg-slate-800">
                  <img
                    src={allProjectImages[currentImageIndex].src}
                    alt={allProjectImages[currentImageIndex].title}
                    className="w-full h-full object-cover transition-opacity duration-500"
                    data-testid="carousel-main-image"
                  />
                  
                  {/* Navigation Arrows */}
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                    data-testid="carousel-prev-button"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                    data-testid="carousel-next-button"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>

                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {allProjectImages.length}
                  </div>
                </div>

                {/* Image Info */}
                <div className="p-6 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-950 dark:to-red-950">
                  <h4 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                    {allProjectImages[currentImageIndex].title}
                  </h4>
                  <p className="text-slate-600 dark:text-slate-300">
                    {allProjectImages[currentImageIndex].description}
                  </p>
                </div>

                {/* Thumbnail Strip */}
                <div className="p-4 bg-slate-100 dark:bg-slate-800">
                  <div className="flex space-x-2 overflow-x-auto pb-2">
                    {allProjectImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                          index === currentImageIndex 
                            ? 'border-orange-500 dark:border-orange-400' 
                            : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                        data-testid={`thumbnail-${index}`}
                      >
                        <img
                          src={image.src}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Auto-play controls */}
            <div className="text-center mt-6">
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                {allProjectImages.length} professional floor preparation projects showcasing our expertise and quality
              </p>
              <div className="flex justify-center space-x-4">
                <Button
                  variant="outline"
                  onClick={prevImage}
                  className="border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-950"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" /> Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={nextImage}
                  className="border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-950"
                >
                  Next <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </motion.section>

        {/* YouTube Videos */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Watch Our Grinding Process
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {youtubeVideos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="relative aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}?hd=1&vq=hd1080&rel=0&modestbranding=1`}
                      title={video.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      data-testid={`youtube-video-${index}`}
                    />
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2 text-slate-800 dark:text-slate-100">
                      {video.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {video.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button
              variant="outline"
              size="lg"
              className="border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-950"
              onClick={() => window.open(COMPANY_INFO.youtube, '_blank')}
              data-testid="view-all-videos-button"
            >
              View All Videos on YouTube <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </motion.section>

        {/* YouTube Shorts */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Quick Floor Prep Tips - YouTube Shorts
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {youtubeShorts.map((short, index) => (
              <motion.div
                key={short.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="relative aspect-[9/16]">
                    <iframe
                      src={`https://www.youtube.com/embed/${short.id}?hd=1&vq=hd1080&rel=0&modestbranding=1`}
                      title={short.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      data-testid={`youtube-short-${index}`}
                    />
                    {/* Shorts Label */}
                    <div className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 text-xs rounded font-medium pointer-events-none z-10">
                      Shorts
                    </div>
                    {/* Share indicator */}
                    <div className="absolute top-3 right-3 bg-black/70 text-white p-1.5 rounded pointer-events-none z-10">
                      <Share className="w-3 h-3" />
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2 text-slate-800 dark:text-slate-100">
                      {short.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {short.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button
              variant="outline"
              size="lg"
              className="border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-950"
              onClick={() => window.open(COMPANY_INFO.youtube, '_blank')}
              data-testid="view-all-shorts-button"
            >
              View All Shorts on YouTube <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </motion.section>

        {/* Contact Form Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="max-w-6xl mx-auto">
            <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
              Request Floor Prep Assessment
            </h3>
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <Card className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="concrete-grinding-contact-form">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 dark:text-slate-200">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                      className="mt-1"
                      data-testid="contact-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-slate-700 dark:text-slate-200">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      className="mt-1"
                      data-testid="contact-email-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 dark:text-slate-200">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+614 XX XXX XXX"
                      className="mt-1"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-slate-700 dark:text-slate-200">Surface Details *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Describe your surface preparation needs - existing coatings, adhesives, unevenness issues, and intended finish..."
                      rows={5}
                      className="mt-1"
                      data-testid="contact-message-textarea"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white border-0"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? 'Sending...' : (
                      <>
                        Send Floor Prep Inquiry <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </Card>
              
              {/* Contact Information */}
              <div className="space-y-8">
                <Card className="p-6 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950 dark:to-red-950 border-orange-200 dark:border-orange-800">
                  <h4 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-100">
                    Surface Preparation Experts
                  </h4>
                  <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
                    With years of experience and industrial-grade equipment, we tackle the toughest 
                    surface preparation challenges. No job is too big or complex for our team.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-orange-600" />
                      <span className="text-slate-700 dark:text-slate-200">{COMPANY_INFO.email}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-orange-600" />
                      <span className="text-slate-700 dark:text-slate-200">{COMPANY_INFO.phone}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-5 h-5 text-orange-600" />
                      <span className="text-slate-700 dark:text-slate-200">{COMPANY_INFO.address}</span>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-6">
                  <h4 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-100">
                    What to Expect
                  </h4>
                  <div className="space-y-3 text-slate-600 dark:text-slate-300">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <span>Detailed assessment of existing surface conditions</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <span>Equipment recommendations based on your specific needs</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <span>Timeline and dust management planning</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <span>Surface profile verification and finish readiness</span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Final CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Card className="p-12 bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-100 dark:to-slate-200 text-white dark:text-slate-900">
            <Wrench className="w-16 h-16 text-orange-400 dark:text-orange-600 mx-auto mb-6" />
            <h3 className="text-3xl font-bold mb-4">
              Start With a Strong Foundation
            </h3>
            <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
              Don't let poor surface preparation compromise your investment. Our professional grinding 
              services ensure your new finish will bond properly and perform for years to come.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white border-0"
                onClick={() => document.querySelector('[data-testid="concrete-grinding-contact-form"]').scrollIntoView({ behavior: 'smooth' })}
                data-testid="final-cta-contact-button"
              >
                Get Surface Assessment <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-white text-white hover:bg-white hover:text-slate-900 dark:border-slate-900 dark:text-slate-900 dark:hover:bg-slate-900 dark:hover:text-white"
                asChild
              >
                <Link to="/services" data-testid="final-cta-services-button">
                  View All Services
                </Link>
              </Button>
            </div>
          </Card>
        </motion.section>
      </div>
    </div>
  );
};

export default ConcreteGrindingPage;